import React from 'react';
import Icon from '../../../components/AppIcon';

const DurationDifficultySlider = ({ duration, difficulty, onDurationChange }) => {
  const durationMarks = [
    { value: 15, label: '15 min', description: 'Quick practice' },
    { value: 30, label: '30 min', description: 'Standard' },
    // Pilot disabled: visible for context, but locked until longer sessions return.
    { value: 45, label: '45 min', description: 'Comprehensive', disabled: true },
    { value: 60, label: '60 min', description: 'Full interview', disabled: true }
  ];
  const selectableDurationMarks = durationMarks.filter(mark => !mark.disabled);
  const minDuration = durationMarks[0].value;
  const maxDuration = durationMarks[durationMarks.length - 1].value;
  const fallbackDuration = selectableDurationMarks[selectableDurationMarks.length - 1].value;
  const isDurationLocked = difficulty === 'medium' || difficulty === 'hard';
  const selectedDuration = selectableDurationMarks.some(mark => mark.value === duration) ? duration : fallbackDuration;
  const currentDuration = isDurationLocked ? 30 : selectedDuration;

  const handleDurationChange = (nextDuration) => {
    if (isDurationLocked) return;
    const selectedMark = durationMarks.find(mark => mark.value === nextDuration);
    if (selectedMark?.disabled) return;
    onDurationChange(nextDuration);
  };

  const currentDurationMark = durationMarks?.find(m => m?.value === currentDuration) || durationMarks?.[1];

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-normal text-slate-900">Interview Duration</label>
          <div className="flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-lg">
            <Icon name="Clock" size={14} className="text-primary" />
            <span className="text-sm font-normal text-primary">{currentDuration} minutes</span>
          </div>
        </div>

        <div className="relative pt-6 pb-2">
          <input
            type="range"
            step="15"
            min={minDuration}
            max={maxDuration}
            value={currentDuration}
            onChange={(e) => handleDurationChange(parseInt(e?.target?.value, 10))}
            disabled={isDurationLocked}
            aria-describedby={isDurationLocked ? 'duration-lock-note' : undefined}
            className={`w-full h-2 bg-slate-300 rounded-lg appearance-none slider-thumb focus:outline-none focus:ring-0 transition-all ${isDurationLocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
            style={{
              background: `linear-gradient(to right, #10E58F 0%, #10E58F ${((currentDuration - minDuration) / (maxDuration - minDuration)) * 100}%, #94a3b8 ${((currentDuration - minDuration) / (maxDuration - minDuration)) * 100}%, #94a3b8 100%)`
            }}
          />
          
          <div className="relative h-10 mt-2">
            {durationMarks?.map((mark) => {
              const percent = ((mark.value - minDuration) / (maxDuration - minDuration)) * 100;
              const isSelected = currentDuration === mark?.value;
              const isDisabled = mark?.disabled || isDurationLocked;
              return (
                <button
                  key={mark?.value}
                  type="button"
                  onClick={() => handleDurationChange(mark?.value)}
                  disabled={isDisabled}
                  title={isDurationLocked ? 'Medium and Hard interviews are fixed at 30 minutes' : isDisabled ? 'Unavailable during pilot' : undefined}
                  className={`absolute flex flex-col items-center gap-1 transition-all duration-300 -translate-x-1/2 ${
                    isSelected ? 'opacity-100 scale-110' : ''
                  } ${
                    isDisabled ? 'opacity-60 cursor-not-allowed' : 'opacity-100 hover:opacity-90'
                  }`}
                  style={{ left: `${percent}%` }}
                >
                  <div className={`w-2 h-2 rounded-full ${isSelected ? 'bg-emerald-600 ring-2 ring-emerald-200' : isDisabled ? 'bg-slate-300' : 'bg-slate-500'}`} />
                  <span className={`text-xs font-normal tracking-tight ${isSelected ? 'text-slate-900' : isDisabled ? 'text-slate-400' : 'text-slate-600'}`}>
                    {mark?.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="py-2.5 px-3 bg-slate-100 rounded-lg border border-slate-200 flex items-center gap-2">
          <Icon name="Info" size={12} className="text-slate-600 shrink-0" />
          <p id={isDurationLocked ? 'duration-lock-note' : undefined} className="text-xs font-normal text-slate-700 leading-relaxed">
            {isDurationLocked
              ? 'Medium and Hard interviews are fixed at 30 minutes.'
              : `${currentDurationMark?.description} session recommended for your selected interview type`}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DurationDifficultySlider;
